'use client';

import React from 'react';
import { Users, UserPlus, UserMinus, TrendingUp } from 'lucide-react';

import { MetricCard } from './MetricCard';
import { ChartContainer } from './ChartContainer';
import { SubscriptionStatusChart } from './SubscriptionStatusChart';
import { useAggregatedMetrics } from '../hooks/useAggregatedMetrics';
import { useActiveSubscriptions } from '../hooks/metrics/useActiveSubscriptions';
import { useSubscriptionsGained } from '../hooks/metrics/useSubscriptionsGained';
import { useSubscriptionsCancelled } from '../hooks/metrics/useSubscriptionsCancelled';
import { EmptyState } from './EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';

interface SubscriberMetricsV4Props {
    data: {
        activeSubscriptionsByDate?: Map<string, number>;
        subscriptionsGainedByDate?: Map<string, number>;
        subscriptionsCancelledByDate?: Map<string, number>;
        subscriptionsByStatus?: {
            totalActive: number;
            active: number;
            scheduledToCancel: number;
            trialing: number;
            pastDue: number;
        };
        subscriptionsByStatusTimeSeries?: Map<string, { active: number; trialing: number; pastDue: number; total: number }>;
    } | null | undefined;
    granularity: 'daily' | 'weekly' | 'monthly';
    loadingStates: {
        rebillRevenue: boolean;
        refunds: boolean;
        straightSale: boolean;
        upsellRevenue: boolean;
        averageLTV: boolean;
        activeSubscriptions: boolean;
        subscriptionsGained: boolean;
        subscriptionsCancelled: boolean;
        approvalRates: boolean;
        subscriptionsByStatus: boolean;
        subscriptionsByStatusTimeSeries: boolean;
    };
    error: any;
}

interface SubscriberSummaryProps {
    title: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    emptyStateMessage?: string;
    isLoading?: boolean;
}

function SubscriberSummary({ title, value, icon: Icon, emptyStateMessage, isLoading }: SubscriberSummaryProps) {
    const isEmpty = value === 0;

    return (
        <div className="flex items-center justify-between p-4 bg-card border rounded-lg shadow-sm">
            <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                    <p className="text-sm font-medium text-muted-foreground">{title}</p>
                    {isLoading ? (
                        <Skeleton className="h-7 w-20" />
                    ) : isEmpty && emptyStateMessage ? (
                        <EmptyState message={emptyStateMessage} variant="inline" />
                    ) : (
                        <p className="text-lg font-semibold text-foreground">{value.toLocaleString()}</p>
                    )}
                </div>
            </div>
        </div>
    );
}

// Progress bar card component for subscription status breakdown
interface SubscriptionStatusBreakdownProps {
    data: {
        totalActive: number;
        active: number;
        scheduledToCancel: number;
        trialing: number;
        pastDue: number;
    };
    isLoading?: boolean;
}

function SubscriptionStatusBreakdown({ data, isLoading }: SubscriptionStatusBreakdownProps) {
    const { t } = useTranslation('analytics');
    if (isLoading) {
        return (
            <div className="h-full flex flex-col justify-center space-y-6 p-4">
                <div className="flex items-center justify-center">
                    <Skeleton className="h-12 w-32" />
                </div>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Skeleton className="h-4 w-16" />
                            <Skeleton className="h-4 w-24" />
                        </div>
                        <Skeleton className="h-3 w-full" />
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Skeleton className="h-4 w-16" />
                            <Skeleton className="h-4 w-24" />
                        </div>
                        <Skeleton className="h-3 w-full" />
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Skeleton className="h-4 w-16" />
                            <Skeleton className="h-4 w-24" />
                        </div>
                        <Skeleton className="h-3 w-full" />
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Skeleton className="h-4 w-16" />
                            <Skeleton className="h-4 w-24" />
                        </div>
                        <Skeleton className="h-3 w-full" />
                    </div>
                </div>
            </div>
        );
    }

    const total = data.totalActive;

    if (total === 0) {
        return <EmptyState message={t('subscribers.noActiveSubscriptions')} />;
    }

    const activePercentage = total > 0 ? (data.active / total) * 100 : 0;
    const scheduledToCancelPercentage = total > 0 ? (data.scheduledToCancel / total) * 100 : 0;
    const trialingPercentage = total > 0 ? (data.trialing / total) * 100 : 0;
    const pastDuePercentage = total > 0 ? (data.pastDue / total) * 100 : 0;

    return (
        <div className="h-full flex flex-col justify-center space-y-6">
            {/* Total Count */}
            <div className="flex items-center justify-center">
                <div className="flex items-center space-x-3">
                    <div>
                        <p className="text-3xl font-bold text-foreground">
                            {total.toLocaleString()}
                        </p>
                    </div>
                </div>
            </div>

            {/* Progress Bars */}
            <div className="space-y-4">
                {/* Active Subscriptions */}
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-muted-foreground">{t('subscribers.active')}</span>
                        <span className="text-sm font-semibold text-foreground">
                            {data.active.toLocaleString()} ({activePercentage.toFixed(1)}%)
                        </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                        <div
                            className="bg-emerald-500 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${activePercentage}%` }}
                        ></div>
                    </div>
                </div>

                {/* Scheduled to Cancel */}
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-muted-foreground">{t('subscribers.scheduledToCancel')}</span>
                        <span className="text-sm font-semibold text-foreground">
                            {data.scheduledToCancel.toLocaleString()} ({scheduledToCancelPercentage.toFixed(1)}%)
                        </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                        <div
                            className="bg-slate-500 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${scheduledToCancelPercentage}%` }}
                        ></div>
                    </div>
                </div>

                {/* Trial Subscriptions */}
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-muted-foreground">{t('subscribers.trial')}</span>
                        <span className="text-sm font-semibold text-foreground">
                            {data.trialing.toLocaleString()} ({trialingPercentage.toFixed(1)}%)
                        </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                        <div
                            className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${trialingPercentage}%` }}
                        ></div>
                    </div>
                </div>

                {/* Past Due Subscriptions */}
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-muted-foreground">{t('subscribers.pastDue')}</span>
                        <span className="text-sm font-semibold text-foreground">
                            {data.pastDue.toLocaleString()} ({pastDuePercentage.toFixed(1)}%)
                        </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                        <div
                            className="bg-amber-500 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${pastDuePercentage}%` }}
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function SubscriberMetricsV4({ data, granularity, loadingStates, error }: SubscriberMetricsV4Props) {
    const { t } = useTranslation('analytics');
    const activeData = useActiveSubscriptions(data?.activeSubscriptionsByDate, granularity);
    const gainedData = useSubscriptionsGained(data?.subscriptionsGainedByDate, granularity);
    const cancelledData = useSubscriptionsCancelled(data?.subscriptionsCancelledByDate, granularity);

    const activeMetrics = useAggregatedMetrics(activeData);
    const gainedMetrics = useAggregatedMetrics(gainedData);
    const cancelledMetrics = useAggregatedMetrics(cancelledData);
    const netGrowth = gainedMetrics.total - cancelledMetrics.total;

    if (error) {
        return (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                <MetricCard title={t('subscribers.errorTitle')} description={t('subscribers.errorDescription')} className="col-span-full">
                    <div className="flex items-center justify-center h-full">
                        <p className="text-destructive">{t('subscribers.errorMessage')}</p>
                    </div>
                </MetricCard>
            </div>
        );
    }

    // Get subscription status breakdown data
    const statusBreakdown = data?.subscriptionsByStatus || {
        totalActive: 0,
        active: 0,
        scheduledToCancel: 0,
        trialing: 0,
        pastDue: 0,
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <SubscriberSummary title={t('subscribers.gained')} value={gainedMetrics.total} icon={UserPlus} emptyStateMessage={t('subscribers.noNewSubscriptions')} isLoading={loadingStates.subscriptionsGained} />
                <SubscriberSummary title={t('subscribers.cancelled')} value={cancelledMetrics.total} icon={UserMinus} emptyStateMessage={t('subscribers.noCancellations')} isLoading={loadingStates.subscriptionsCancelled} />
                <SubscriberSummary title={t('subscribers.netGrowth')} value={netGrowth} icon={TrendingUp} emptyStateMessage={t('subscribers.noGrowth')} isLoading={loadingStates.subscriptionsGained || loadingStates.subscriptionsCancelled} />
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                <MetricCard
                    title={t('subscribers.totalSubscriptions')}
                    description={t('subscribers.totalSubscriptionsDescription')}
                    isLoading={loadingStates.subscriptionsByStatus}
                >
                    <SubscriptionStatusBreakdown
                        data={statusBreakdown}
                        isLoading={loadingStates.subscriptionsByStatus}
                    />
                </MetricCard>

                <MetricCard
                    title={t('subscribers.statusOverTime')}
                    description={t('subscribers.statusOverTimeDescription')}
                    isLoading={loadingStates.subscriptionsByStatusTimeSeries}
                >
                    {data?.subscriptionsByStatusTimeSeries ? (
                        <SubscriptionStatusChart data={data.subscriptionsByStatusTimeSeries} />
                    ) : (
                        <EmptyState message={t('subscribers.noStatusData')} />
                    )}
                </MetricCard>

                <MetricCard title={t('subscribers.gained')} description={t('subscribers.gainedDescription')} isLoading={loadingStates.subscriptionsGained}>
                    <ChartContainer data={gainedData} chartType="bar" emptyStateMessage={t('subscribers.noGained')} />
                </MetricCard>

                <MetricCard title={t('subscribers.cancelled')} description={t('subscribers.cancelledDescription')} isLoading={loadingStates.subscriptionsCancelled}>
                    <ChartContainer data={cancelledData} chartType="bar" emptyStateMessage={t('subscribers.noCancellations')} />
                </MetricCard>
            </div>
        </div>
    );
}


